import { FunctionComponent } from "react";

import { Heading, Stack } from "@bps/fluent-ui";
import {
  UserNetworkAuthenticationSettingsDto,
  UserNetworkAuthenticationSettingsPayload
} from "@libs/api/gateways/plt/plt-gateway.dtos";
import {
  useAddUserNetworkAuthenticationSetting,
  useDeleteUserNetworkAuthenticationSetting,
  useToggleUserNetworkAuthenticationSetting,
  useUpdateUserNetworkAuthenticationSetting
} from "@libs/api/gateways/plt/plt-gateway.hooks";
import { useFeedbackContext } from "../../../../../../../libs/stores/feedback-context/feedback-context";

import {
  AddIpRangeFormValues,
  NetworkAuthenticationSettingsSection
} from "../../../_shared-blocks";

interface UserIpRangesSectionProps {
  hasWritePermission: boolean;
  tenantId: string;
  userId: string;
  networkSettings: UserNetworkAuthenticationSettingsDto[];
}

export const UserIpRangesSection: FunctionComponent<UserIpRangesSectionProps> = ({
  hasWritePermission,
  tenantId,
  networkSettings,
  userId
}) => {
  const { error } = useFeedbackContext();

  const {
    mutateAsync: addNetworkSetting
  } = useAddUserNetworkAuthenticationSetting();

  const {
    mutateAsync: updateNetworkSetting
  } = useUpdateUserNetworkAuthenticationSetting();

  const {
    mutateAsync: deleteNetworkSetting
  } = useDeleteUserNetworkAuthenticationSetting(tenantId, userId);

  const {
    mutateAsync: toggleNetworkSetting
  } = useToggleUserNetworkAuthenticationSetting();

  const handleAddIpRange = async (
    values: AddIpRangeFormValues,
    id?: string
  ) => {
    const payload: UserNetworkAuthenticationSettingsPayload = {
      tenantId,
      userId,
      isActive: true,
      startAddress: values.startAddress,
      endAddress: values.endAddress,
      description: values.description
    };

    if (networkSettings && id) {
      await updateNetworkSetting({ id, payload });
    } else {
      await addNetworkSetting(payload);
    }
  };

  const handleRemoveIpRange = async (id: string) => {
    try {
      await deleteNetworkSetting(id);
    } catch (e) {
      error(e);
    }
  };

  const handleToggleIpRange = async (id: string, shouldDisable: boolean) => {
    try {
      await toggleNetworkSetting({ id, disable: shouldDisable });
    } catch (e) {
      error(e);
    }
  };

  return (
    <Stack tokens={{ childrenGap: 16 }}>
      <Heading variant="section-heading">Allow Listed IP Addresses</Heading>
      <NetworkAuthenticationSettingsSection
        hasWritePermission={hasWritePermission}
        tenantId={tenantId}
        networkSettings={networkSettings}
        handleRemoveIpRange={handleRemoveIpRange}
        handleAddIpRange={handleAddIpRange}
        handleToggleIpRange={handleToggleIpRange}
      />
    </Stack>
  );
};
